import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Box, Spinner, VStack, Text } from '@chakra-ui/react'
import { useAuth } from '../../contexts/AuthContext'
import { useProfiles } from '../../hooks/useProfiles'
import { API_BASE_URL } from '../../config/api'
import { resolveLastAuthenticatedDestination } from '../../utils/authRedirect'

export const PostLoginRedirect: React.FC = () => {
  const { defaultProfile, loading, isAuthenticated, currentUser, userAccount } = useAuth()
  const { allProfiles, isLoading: profilesLoading } = useProfiles()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [retryCount, setRetryCount] = useState(0)
  const [checkingMigration, setCheckingMigration] = useState(true)
  const maxRetries = 5 // Fewer retries with exponential backoff

  // Check if migration is in progress on mount
  useEffect(() => {
    const checkMigrationStatus = async () => {
      if (!currentUser) {
        setCheckingMigration(false)
        return
      }

      try {
        const token = await currentUser.getIdToken(true)
        const res = await fetch(`${API_BASE_URL}/api/v1/migration/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        })

        if (res.ok) {
          const data = await res.json()
          // If migration is actively running, redirect to migration wait page
          if (data.migrating && data.status === 'in_progress') {
            navigate('/migration-wait', { replace: true })
            return
          }
        }
      } catch (error) {
        console.log('Migration check failed or not applicable:', error)
      } finally {
        setCheckingMigration(false)
      }
    }

    checkMigrationStatus()
  }, [currentUser, navigate])

  useEffect(() => {
    // Wait for migration check to complete
    if (checkingMigration) return

    console.log('PostLoginRedirect debug:', {
      loading,
      isAuthenticated,
      userAccount: !!userAccount,
      defaultProfile: defaultProfile?.profileId,
      profilesLoading,
      allProfilesCount: allProfiles.length,
      retryCount
    })

    if (!loading && isAuthenticated) {
      // Check if user came from DNA kit purchase (from URL params or location state)
      const source = searchParams.get('source') || location.state?.source
      if (source === 'dna-kit-purchase') {
        navigate('/account?tab=dna-kits&source=dna-kit-purchase', { replace: true })
        return
      }

      const product = searchParams.get('product')
      const plan = searchParams.get('plan')
      const interval = searchParams.get('interval') || 'monthly'
      if (product === 'insight_pass') {
        navigate(`/billing/checkout?plan=basic&interval=quarterly&source=${encodeURIComponent(source || 'post_login')}`, { replace: true })
        return
      }
      if (plan && (plan === 'basic' || plan === 'premium')) {
        navigate(`/billing/checkout?plan=${encodeURIComponent(plan)}&interval=${encodeURIComponent(interval)}&source=${encodeURIComponent(source || 'post_login')}`, { replace: true })
        return
      }
      
      // Check if there's a specific destination in location state
      const intendedFrom = location.state?.from
      const intendedDestination = intendedFrom?.pathname
      const intendedSearch = intendedFrom?.search || ''

      // Check for a pending named prompt (e.g. from Phoenix partner page)
      const getDefaultDestination = () => {
        try {
          const stored = localStorage.getItem('lw_prompt')
          if (stored) {
            const parsed = JSON.parse(stored)
            if (parsed?.slug && Date.now() - parsed.ts < 60 * 60 * 1000) {
              return `/chat?prompt=${encodeURIComponent(parsed.slug)}`
            }
          }
        } catch {
          // ignore malformed storage
        }
        return resolveLastAuthenticatedDestination(currentUser?.uid ?? '')
      }

      // First priority: Use defaultProfile from AuthContext
      if (defaultProfile?.profileId) {
        // If there's an intended destination, go there instead of dashboard
        // Note: If it's a Pro route, RequireProAccessInner will handle the redirect with proRequired flag
        if (intendedDestination && intendedDestination !== '/redirect-to-profile') {
          navigate(`${intendedDestination}${intendedSearch}`, { replace: true })
        } else {
          navigate(getDefaultDestination(), { replace: true })
        }
        return
      }

      // Second priority: Use first available profile from useProfiles
      if (!profilesLoading && allProfiles.length > 0) {
        // If there's an intended destination, go there instead of dashboard
        // Note: If it's a Pro route, RequireProAccessInner will handle the redirect with proRequired flag
        if (intendedDestination && intendedDestination !== '/redirect-to-profile') {
          navigate(`${intendedDestination}${intendedSearch}`, { replace: true })
        } else {
          navigate(getDefaultDestination(), { replace: true })
        }
        return
      }

      // Third priority: Retry if profiles are still loading or not yet available
      if (profilesLoading || (allProfiles.length === 0 && retryCount < maxRetries)) {
        
        // Exponential backoff: 200ms, 400ms, 800ms, 1600ms, 3200ms
        const backoffDelay = Math.min(200 * Math.pow(2, retryCount), 3200)
        
        const timer = setTimeout(() => {
          setRetryCount(prev => prev + 1)
        }, backoffDelay)
        return () => clearTimeout(timer)
      }

      // Final fallback: send user to Account page where they can create/select a profile
      navigate('/account', { replace: true })
    } else if (!loading && !isAuthenticated) {
      // User is not authenticated, redirect to login
      navigate('/login', { 
        replace: true,
        state: { from: location }
      })
    }
  }, [checkingMigration, loading, isAuthenticated, currentUser?.uid, userAccount, defaultProfile, profilesLoading, allProfiles, navigate, location, searchParams, retryCount, maxRetries])

  // Show loading spinner while determining where to redirect
  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="bg"
    >
      <VStack gap={4}>
        <Spinner
          color="brand.500"
          size="xl"
        />
        <Text color="fg.muted" fontSize="sm">
          {profilesLoading || retryCount > 0 ? 'Loading your profile...' : 'Setting up your account...'}
        </Text>
      </VStack>
    </Box>
  )
} 
